"use client";

import * as React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";
const services = [{
  id: "item-1",
  title: "Customs Compliance",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/customs_clearance_documents_01.png",
  description: "Navigating complex international regulations to ensure seamless border crossings. We provide comprehensive audits, documentation support, and risk mitigation strategies for global trade operations.",
  specs: ["HS Code Verification", "Duty Drawback Programs", "Customs Valuation", "Post-Entry Audits"],
  link: "/RegulatoryPage"
}, {
  id: "item-2",
  title: "Tariff Classification",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/shipping_containers_port_02.png",
  description: "Precise identification of goods to minimize duty liabilities and ensure legal adherence. Our experts analyze product compositions against global Harmonized System standards to optimize your bottom line.",
  specs: ["HS/HTS Classification", "Binding Tariff Rulings", "Duty Rate Analysis", "Country of Origin Marking"],
  link: "/TradeServicesPage"
}, {
  id: "item-3",
  title: "Export Licensing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/legal_compliance_stamp_03.png",
  description: "Strategic management of controlled goods and technology transfers. We help secure necessary permits and navigate dual-use regulations, sanctions, and international trade embargos.",
  specs: ["EAR/ITAR Compliance", "Denied Party Screening", "End-User Verification", "License Application"],
  link: "/RegulatoryPage"
}, {
  id: "item-4",
  title: "Supply Chain Security",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/secure_warehouse_logistics_04.png",
  description: "Enhancing the integrity of your global logistics through recognized security programs. We prepare businesses for international certifications that expedite clearance and reduce inspection rates.",
  specs: ["C-TPAT Certification", "AEO Status Advisory", "Risk Assessments", "Security Audits"],
  link: "/MarketEntryPage"
}, {
  id: "item-5",
  title: "Trade Agreements",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/global_trade_map_05.png",
  description: "Leveraging Free Trade Agreements (FTAs) to gain a competitive edge. We verify eligibility and manage certificates of origin to maximize cost savings across your international supply chain.",
  specs: ["FTA Eligibility Analysis", "Rules of Origin Reviews", "Certificate Management", "Supplier Solicitations"],
  link: "/MarketEntryPage"
}];
export default function ServiceDetails() {
  return <section data-section-id="5000" id="exportimport-advisory-compliance-guidance" className="bg-background text-foreground py-24 min-h-screen flex flex-col justify-center">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="mb-16 border-b border-border pb-6">
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-2">
            Advisory
          </h2>
          <h3 className="text-3xl md:text-5xl font-bold tracking-tighter text-foreground font-serif">
            Export-Import Guidance
          </h3>
        </div>

        <Accordion type="single" collapsible className="w-full border-t border-border">
          {services.map((service, i) => <AccordionItem key={service.id} value={service.id} className="border-b border-border last:border-b-0 data-[state=open]:border-border" data-index={i}>
              <AccordionTrigger className="py-8 md:py-10 hover:no-underline group transition-colors duration-300">
                <span className="text-2xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground group-hover:text-secondary transition-colors duration-300 text-left">
                  {service.title}
                </span>
              </AccordionTrigger>
              
              <AccordionContent className="pb-8 md:pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start pt-4">
                  {/* Image Column */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-muted">
                    <img src={service.image} alt={`${service.title} specialized advisory`} className="object-cover w-full h-full opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />
                    <div className="absolute bottom-4 left-4 font-mono text-xs text-foreground/70 bg-background/80 px-2 py-1 backdrop-blur-sm rounded-sm">
                      REF_DOC_{i + 1}0{i + 1}.PDF
                    </div>
                  </div>

                  {/* Content Column */}
                  <div className="flex flex-col justify-between h-full space-y-8">
                    <div>
                      <h4 className="text-lg font-medium text-secondary mb-4 font-mono uppercase tracking-wider">
                        Compliance Metrics
                      </h4>
                      <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-xl">
                        {service.description}
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 border-t border-border pt-6">
                        {service.specs.map((spec, idx) => <div key={idx} className="flex items-start gap-3 group/spec">
                            <CheckCircle2 className="w-5 h-5 text-muted-foreground group-hover/spec:text-secondary transition-colors mt-0.5 shrink-0" />
                            <span className="text-foreground font-mono text-sm">
                              {spec}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    <div className="pt-6">
                      <Button asChild variant="outline" className="rounded-md border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 uppercase tracking-widest text-xs h-12 px-8">
                        <Link to={service.link}>
                          View Details <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}